import { supabase } from '../../lib/supabase'
import { failure, type AccountResult } from './accountStore'

/**
 * The shift template and status code catalogs an administrator manages, read
 * straight from muster.shift_templates and muster.status_codes (any signed-in
 * account reads them) and changed only through v1-create-shift-template,
 * v1-update-shift-template, and v1-update-status-code, which check the caller is
 * an active administrator and audit each change.
 */
export const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export type ShiftTemplate = {
  id: string
  name: string
  startTime: string // HH:MM
  endTime: string // HH:MM
  mealMinutes: number
  weekdays: number[] // 0 = Sunday .. 6 = Saturday
  teamId: string | null // null is office-wide
  teamName: string
  active: boolean
  placements: number // employee placements on this shift
  targets: number // staffing targets naming it
}

type ShiftRow = {
  id: string
  name: string
  start_time: string
  end_time: string
  meal_minutes: number | null
  weekdays: number[]
  team_id: string | null
  active: boolean
  teams: { name: string } | null
}

// "07:00:00" as "07:00".
function clock(time: string): string {
  return time.slice(0, 5)
}

function tally(rows: { shift_template_id: string | null }[] | null): Map<string, number> {
  const counts = new Map<string, number>()
  for (const row of rows ?? []) {
    if (row.shift_template_id) counts.set(row.shift_template_id, (counts.get(row.shift_template_id) ?? 0) + 1)
  }
  return counts
}

export async function getShiftTemplates(): Promise<ShiftTemplate[]> {
  const [shifts, placements, targets] = await Promise.all([
    supabase
      .from('shift_templates')
      .select('id, name, start_time, end_time, meal_minutes, weekdays, team_id, active, teams(name)')
      .order('name', { ascending: true }),
    supabase.from('employee_assignments').select('shift_template_id'),
    supabase.from('staffing_targets').select('shift_template_id'),
  ])
  if (shifts.error || !shifts.data) return []
  const placementCounts = tally(placements.data as { shift_template_id: string | null }[] | null)
  const targetCounts = tally(targets.data as { shift_template_id: string | null }[] | null)
  return (shifts.data as unknown as ShiftRow[]).map((row) => ({
    id: row.id,
    name: row.name,
    startTime: clock(row.start_time),
    endTime: clock(row.end_time),
    mealMinutes: row.meal_minutes ?? 0,
    weekdays: [...row.weekdays].sort((a, b) => a - b),
    teamId: row.team_id,
    teamName: row.teams?.name ?? '',
    active: row.active,
    placements: placementCounts.get(row.id) ?? 0,
    targets: targetCounts.get(row.id) ?? 0,
  }))
}

export type ShiftTemplateValues = {
  name: string
  startTime: string
  endTime: string
  mealMinutes: number
  weekdays: number[]
  teamId: string | null
}

async function invoke(name: string, body: Record<string, unknown>, fallback: string): Promise<AccountResult> {
  const { error } = await supabase.functions.invoke(name, { body })
  return error ? failure(error, fallback) : { ok: true }
}

export function createShiftTemplate(values: ShiftTemplateValues): Promise<AccountResult> {
  return invoke('v1-create-shift-template', values, 'The shift template could not be created.')
}

export function updateShiftTemplate(id: string, values: ShiftTemplateValues, active: boolean): Promise<AccountResult> {
  return invoke('v1-update-shift-template', { shiftTemplateId: id, ...values, active }, 'The shift template could not be updated.')
}

/**
 * "Mon to Fri", "Mon, Wed, Fri", or "Every day": the days a shift runs, with
 * a run of consecutive days written as a range.
 */
export function weekdaysLabel(weekdays: number[]): string {
  const days = [...weekdays].sort((a, b) => a - b)
  if (days.length === 7) return 'Every day'
  if (days.length > 2 && days.every((day, index) => index === 0 || day === days[index - 1] + 1)) {
    return `${WEEKDAY_LABELS[days[0]]} to ${WEEKDAY_LABELS[days[days.length - 1]]}`
  }
  return days.map((day) => WEEKDAY_LABELS[day]).join(', ')
}

export const STATUS_CATEGORIES: { key: StatusCategory; label: string }[] = [
  { key: 'work', label: 'Work' },
  { key: 'off', label: 'Off' },
  { key: 'leave', label: 'Leave' },
  { key: 'training', label: 'Training' },
  { key: 'detail', label: 'Detail' },
  { key: 'unavailable', label: 'Unavailable' },
]

export type StatusCategory = 'work' | 'off' | 'leave' | 'training' | 'detail' | 'unavailable'

export type StatusCode = {
  id: string
  code: string
  name: string
  shortLabel: string
  category: StatusCategory
  countsAsAvailable: boolean
  sortOrder: number
  entries: number // schedule entries that use it
}

type StatusRow = {
  id: string
  code: string
  name: string
  short_label: string
  category: StatusCategory
  counts_as_available: boolean
  sort_order: number
}

export async function getStatusCodes(): Promise<StatusCode[]> {
  const { data, error } = await supabase
    .from('status_codes')
    .select('id, code, name, short_label, category, counts_as_available, sort_order')
    .order('sort_order', { ascending: true })
  if (error || !data) return []
  const rows = data as unknown as StatusRow[]
  // A count per code rather than every entry: the schedule holds far more
  // rows than the catalog has codes.
  const entries = await Promise.all(
    rows.map(async (row) => {
      const { count } = await supabase.from('schedule_entries').select('id', { count: 'exact', head: true }).eq('status_code_id', row.id)
      return count ?? 0
    }),
  )
  return rows.map((row, index) => ({
    id: row.id,
    code: row.code,
    name: row.name,
    shortLabel: row.short_label,
    category: row.category,
    countsAsAvailable: row.counts_as_available,
    sortOrder: row.sort_order,
    entries: entries[index],
  }))
}

export type StatusCodeValues = {
  shortLabel: string
  category: StatusCategory
  countsAsAvailable: boolean
  sortOrder: number
}

export function updateStatusCode(id: string, values: StatusCodeValues): Promise<AccountResult> {
  return invoke('v1-update-status-code', { statusCodeId: id, ...values }, 'The status code could not be updated.')
}

export function categoryLabel(category: StatusCategory): string {
  return STATUS_CATEGORIES.find((option) => option.key === category)?.label ?? category
}

// Activities and assignments -----------------------------------------------------

export type Activity = {
  id: string
  code: string
  name: string
  parentId: string | null
  parentName: string
  active: boolean
  // "Case work" for an activity, "Case work › Intake review" for a sub-activity.
  path: string
  openChildren: number
}

type ActivityRow = { id: string; code: string; name: string; parent_activity_id: string | null; active: boolean }

/**
 * Every activity, each top-level one followed by its sub-activities, so the
 * list reads as the two-level catalog it is.
 */
export async function getActivities(): Promise<Activity[]> {
  const { data, error } = await supabase.from('activities').select('id, code, name, parent_activity_id, active')
  if (error || !data) return []
  const rows = data as unknown as ActivityRow[]
  const byName = (a: ActivityRow, b: ActivityRow) => a.name.localeCompare(b.name)
  const names = new Map(rows.map((row) => [row.id, row.name]))
  const activity = (row: ActivityRow): Activity => {
    const parentName = row.parent_activity_id ? (names.get(row.parent_activity_id) ?? '') : ''
    return {
      id: row.id,
      code: row.code,
      name: row.name,
      parentId: row.parent_activity_id,
      parentName,
      active: row.active,
      path: parentName ? `${parentName} › ${row.name}` : row.name,
      openChildren: rows.filter((child) => child.parent_activity_id === row.id && child.active).length,
    }
  }
  return rows
    .filter((row) => row.parent_activity_id === null)
    .sort(byName)
    .flatMap((parent) => [activity(parent), ...rows.filter((child) => child.parent_activity_id === parent.id).sort(byName).map(activity)])
}

export type ActivityValues = { code: string; name: string; parentId: string | null }

export function createActivity(values: ActivityValues): Promise<AccountResult> {
  return invoke('v1-create-activity', values, 'The activity could not be created.')
}

export function updateActivity(id: string, name: string, active: boolean): Promise<AccountResult> {
  return invoke('v1-update-activity', { activityId: id, name, active }, 'The activity could not be updated.')
}

export type Qualification = { id: string; name: string }

// The qualifications an assignment can require: the active ones.
export async function getQualifications(): Promise<Qualification[]> {
  const { data, error } = await supabase.from('qualifications').select('id, name').eq('active', true).order('name', { ascending: true })
  if (error || !data) return []
  return data as Qualification[]
}

export type Assignment = {
  id: string
  code: string
  name: string
  dutyCategory: string
  teamId: string | null
  teamName: string
  sortPriority: number
  requiredQualificationId: string | null
  qualificationName: string
  defaultActivityId: string | null
  active: boolean
  entries: number // schedule entries that carry it
  targets: number // staffing targets that name it
}

type AssignmentRow = {
  id: string
  code: string
  name: string
  duty_category: string
  team_id: string | null
  sort_priority: number
  required_qualification_id: string | null
  default_activity_id: string | null
  active: boolean
  teams: { name: string } | null
  qualifications: { name: string } | null
}

export async function getAssignments(): Promise<Assignment[]> {
  const { data, error } = await supabase
    .from('assignments')
    .select('id, code, name, duty_category, team_id, sort_priority, required_qualification_id, default_activity_id, active, teams(name), qualifications(name)')
    .order('sort_priority', { ascending: true })
  if (error || !data) return []
  const rows = data as unknown as AssignmentRow[]
  const targets = await supabase.from('staffing_targets').select('assignment_id')
  const targetCounts = new Map<string, number>()
  for (const row of (targets.data ?? []) as { assignment_id: string | null }[]) {
    if (row.assignment_id) targetCounts.set(row.assignment_id, (targetCounts.get(row.assignment_id) ?? 0) + 1)
  }
  // A count per assignment rather than every entry: the schedule holds far
  // more rows than the catalog has assignments.
  const entries = await Promise.all(
    rows.map(async (row) => {
      const { count } = await supabase.from('schedule_entries').select('id', { count: 'exact', head: true }).eq('assignment_id', row.id)
      return count ?? 0
    }),
  )
  return rows
    .map((row, index) => ({
      id: row.id,
      code: row.code,
      name: row.name,
      dutyCategory: row.duty_category,
      teamId: row.team_id,
      teamName: row.teams?.name ?? '',
      sortPriority: row.sort_priority,
      requiredQualificationId: row.required_qualification_id,
      qualificationName: row.qualifications?.name ?? '',
      defaultActivityId: row.default_activity_id,
      active: row.active,
      entries: entries[index],
      targets: targetCounts.get(row.id) ?? 0,
    }))
    .sort((a, b) => a.sortPriority - b.sortPriority || a.name.localeCompare(b.name))
}

export type AssignmentValues = {
  dutyCategory: string
  teamId: string | null
  sortPriority: number
  requiredQualificationId: string | null
  defaultActivityId: string | null
}

export function createAssignment(values: AssignmentValues & { code: string; name: string }): Promise<AccountResult> {
  return invoke('v1-create-assignment', values, 'The assignment could not be created.')
}

export function updateAssignment(id: string, values: AssignmentValues, active: boolean): Promise<AccountResult> {
  return invoke('v1-update-assignment', { assignmentId: id, ...values, active }, 'The assignment could not be updated.')
}

// Leave types ----------------------------------------------------------------------

export type AdminLeaveType = {
  id: string
  name: string
  active: boolean
  requests: number // leave requests that name it
}

// Every leave type, retired ones too, with how many requests name each.
export async function getAdminLeaveTypes(): Promise<AdminLeaveType[]> {
  const { data, error } = await supabase.from('leave_types').select('id, name, active').order('name', { ascending: true })
  if (error || !data) return []
  const rows = data as { id: string; name: string; active: boolean }[]
  // A count per type rather than every request: the requests far outnumber the types.
  const requests = await Promise.all(
    rows.map(async (row) => {
      const { count } = await supabase.from('leave_requests').select('id', { count: 'exact', head: true }).eq('leave_type_id', row.id)
      return count ?? 0
    }),
  )
  return rows.map((row, index) => ({ id: row.id, name: row.name, active: row.active, requests: requests[index] }))
}

export function createLeaveType(name: string): Promise<AccountResult> {
  return invoke('v1-create-leave-type', { name }, 'The leave type could not be created.')
}

export function updateLeaveType(id: string, name: string, active: boolean): Promise<AccountResult> {
  return invoke('v1-update-leave-type', { leaveTypeId: id, name, active }, 'The leave type could not be updated.')
}
